/**
 * Rax Cost Breakdown Component
 *
 * Shows cost breakdown by time period, category, and skill.
 */

import { LitElement, html, css } from "lit";
import { customElement, property, state } from "lit/decorators.js";

export interface CostSummary {
  totalUsd: number;
  byCategory: Record<string, number>;
  bySkill: Record<string, number>;
  byModel: Record<string, number>;
  totalTokens: {
    input: number;
    output: number;
    thinking: number;
  };
  actionCount: number;
  period: {
    start: string;
    end: string;
  };
}

type Period = "today" | "week" | "month" | "all";

@customElement("rax-cost-breakdown")
export class RaxCostBreakdown extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }
    
    .breakdown-container {
      background: var(--card-bg, #fff);
      border: 1px solid var(--card-border, #e5e7eb);
      border-radius: 12px;
      overflow: hidden;
    }
    
    .breakdown-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 20px;
      background: var(--header-bg, #f8fafc);
      border-bottom: 1px solid var(--card-border, #e5e7eb);
    }
    
    .breakdown-title {
      font-size: 16px;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    
    .period-tabs {
      display: flex;
      gap: 4px;
      background: var(--tabs-bg, #e5e7eb);
      padding: 3px;
      border-radius: 8px;
    }
    
    .period-tab {
      padding: 6px 12px;
      border: none;
      border-radius: 6px;
      background: transparent;
      font-size: 12px;
      cursor: pointer;
      transition: all 0.2s;
    }
    
    .period-tab:hover {
      background: var(--tab-hover, #d1d5db);
    }
    
    .period-tab.active {
      background: var(--tab-active, #fff);
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }
    
    .breakdown-content {
      padding: 20px;
    }
    
    .total-section {
      text-align: center;
      margin-bottom: 24px;
      padding-bottom: 24px;
      border-bottom: 1px solid var(--card-border, #e5e7eb);
    }
    
    .total-amount {
      font-size: 42px;
      font-weight: 700;
      font-family: monospace;
      color: var(--primary-color, #6366f1);
    }
    
    .total-label {
      font-size: 14px;
      color: var(--text-secondary, #6b7280);
      margin-top: 4px;
    }
    
    .stats-row {
      display: flex;
      justify-content: center;
      gap: 32px;
      margin-top: 16px;
    }
    
    .stat-item {
      text-align: center;
    }
    
    .stat-value {
      font-size: 18px;
      font-weight: 600;
    }
    
    .stat-label {
      font-size: 11px;
      color: var(--text-secondary, #6b7280);
    }
    
    .section {
      margin-bottom: 24px;
    }
    
    .section:last-child {
      margin-bottom: 0;
    }
    
    .section-title {
      font-size: 12px;
      font-weight: 600;
      text-transform: uppercase;
      letter-spacing: 0.5px;
      color: var(--text-tertiary, #9ca3af);
      margin-bottom: 12px;
    }
    
    .breakdown-list {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    
    .breakdown-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 10px 12px;
      background: var(--item-bg, #f9fafb);
      border-radius: 8px;
    }
    
    .item-name {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 14px;
    }
    
    .item-icon {
      font-size: 16px;
    }
    
    .item-cost {
      font-family: monospace;
      font-weight: 500;
    }
    
    .item-bar-container {
      flex: 1;
      margin: 0 16px;
      height: 4px;
      background: var(--bar-bg, #e5e7eb);
      border-radius: 2px;
      overflow: hidden;
    }
    
    .item-bar {
      height: 100%;
      background: var(--primary-color, #6366f1);
      border-radius: 2px;
      transition: width 0.3s;
    }
    
    .item-percent {
      font-size: 12px;
      color: var(--text-secondary, #6b7280);
      min-width: 40px;
      text-align: right;
    }
    
    .empty-state {
      text-align: center;
      padding: 40px;
      color: var(--text-secondary, #6b7280);
    }
    
    .budget-warning {
      margin-top: 16px;
      padding: 12px 16px;
      background: var(--warning-bg, #fef3c7);
      border-radius: 8px;
      display: flex;
      align-items: center;
      gap: 12px;
    }
    
    .budget-warning.danger {
      background: var(--danger-bg, #fee2e2);
    }
    
    .budget-warning-icon {
      font-size: 20px;
    }
    
    .budget-warning-text {
      flex: 1;
      font-size: 13px;
    }
    
    /* Dark mode */
    @media (prefers-color-scheme: dark) {
      .breakdown-container {
        background: var(--card-bg, #1f2937);
        border-color: var(--card-border, #374151);
      }
    
      .breakdown-header {
        background: var(--header-bg, #111827);
      }
    
      .period-tabs {
        background: var(--tabs-bg, #374151);
      }
    
      .period-tab.active {
        background: var(--tab-active, #4b5563);
      }
    
      .breakdown-item {
        background: var(--item-bg, #111827);
      }
    }
  `;

  @property({ type: Object })
  summary: CostSummary | null = null;

  @property({ type: Number })
  dailyBudget?: number;

  @property({ type: Number })
  monthlyBudget?: number;

  @state()
  private period: Period = "today";

  override render() {
    return html`
      <div class="breakdown-container">
        <div class="breakdown-header">
          <div class="breakdown-title">
            <span>💰</span>
            <span>Cost Breakdown</span>
          </div>
          <div class="period-tabs">
            ${this.renderPeriodTab("today", "Today")}
            ${this.renderPeriodTab("week", "Week")}
            ${this.renderPeriodTab("month", "Month")}
            ${this.renderPeriodTab("all", "All Time")}
          </div>
        </div>

        <div class="breakdown-content">
          ${this.summary ? this.renderBreakdown() : this.renderEmpty()}
        </div>
      </div>
    `;
  }

  private renderPeriodTab(value: Period, label: string) {
    return html`
      <button
        class="period-tab ${this.period === value ? "active" : ""}"
        @click=${() => this.onPeriodChange(value)}
      >${label}</button>
    `;
  }

  private renderBreakdown() {
    const { totalUsd, byCategory, bySkill, byModel, totalTokens, actionCount } = this.summary!;

    return html`
      <div class="total-section">
        <div class="total-amount">${this.formatCost(totalUsd)}</div>
        <div class="total-label">${this.getPeriodLabel()}</div>
        <div class="stats-row">
          <div class="stat-item">
            <div class="stat-value">${actionCount.toLocaleString()}</div>
            <div class="stat-label">Actions</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${this.formatTokens(totalTokens.input)}</div>
            <div class="stat-label">Input Tokens</div>
          </div>
          <div class="stat-item">
            <div class="stat-value">${this.formatTokens(totalTokens.output)}</div>
            <div class="stat-label">Output Tokens</div>
          </div>
        </div>
      </div>

      ${this.renderBudgetWarning(totalUsd)}

      ${
        Object.keys(byCategory).length > 0
          ? html`
        <div class="section">
          <div class="section-title">By Category</div>
          <div class="breakdown-list">
            ${this.renderBreakdownItems(byCategory, totalUsd, this.getCategoryIcon)}
          </div>
        </div>
      `
          : ""
      }

      ${
        Object.keys(bySkill).length > 0
          ? html`
        <div class="section">
          <div class="section-title">By Skill</div>
          <div class="breakdown-list">
            ${this.renderBreakdownItems(bySkill, totalUsd, this.getSkillIcon)}
          </div>
        </div>
      `
          : ""
      }

      ${
        Object.keys(byModel).length > 0
          ? html`
        <div class="section">
          <div class="section-title">By Model</div>
          <div class="breakdown-list">
            ${this.renderBreakdownItems(byModel, totalUsd, () => "🤖")}
          </div>
        </div>
      `
          : ""
      }
    `;
  }

  private renderBreakdownItems(
    data: Record<string, number>,
    total: number,
    getIcon: (key: string) => string,
  ) {
    const sorted = Object.entries(data).sort((a, b) => b[1] - a[1]);

    return sorted.map(([name, cost]) => {
      const percent = total > 0 ? (cost / total) * 100 : 0;
      return html`
        <div class="breakdown-item">
          <div class="item-name">
            <span class="item-icon">${getIcon(name)}</span>
            <span>${this.formatName(name)}</span>
          </div>
          <div class="item-bar-container">
            <div class="item-bar" style="width: ${percent}%"></div>
          </div>
          <span class="item-percent">${percent.toFixed(0)}%</span>
          <span class="item-cost">${this.formatCost(cost)}</span>
        </div>
      `;
    });
  }

  private renderBudgetWarning(totalUsd: number) {
    const budget =
      this.period === "today"
        ? this.dailyBudget
        : this.period === "month"
          ? this.monthlyBudget
          : undefined;

    if (!budget) return "";

    const percent = (totalUsd / budget) * 100;

    if (percent >= 100) {
      return html`
        <div class="budget-warning danger">
          <span class="budget-warning-icon">🚨</span>
          <span class="budget-warning-text">
            Budget exceeded! You've spent ${this.formatCost(totalUsd)} of your ${this.formatCost(budget)} budget.
          </span>
        </div>
      `;
    }

    if (percent >= 80) {
      return html`
        <div class="budget-warning">
          <span class="budget-warning-icon">⚠️</span>
          <span class="budget-warning-text">
            Approaching budget limit (${percent.toFixed(0)}% used)
          </span>
        </div>
      `;
    }

    return "";
  }

  private renderEmpty() {
    return html`
      <div class="empty-state">
        <p>No cost data for this period</p>
      </div>
    `;
  }

  private onPeriodChange(period: Period) {
    this.period = period;
    this.dispatchEvent(
      new CustomEvent("period-change", {
        bubbles: true,
        detail: { period },
      }),
    );
  }

  private getPeriodLabel(): string {
    switch (this.period) {
      case "today":
        return "Today";
      case "week":
        return "This Week";
      case "month":
        return "This Month";
      case "all":
        return "All Time";
    }
  }

  private formatCost(usd: number): string {
    if (usd === 0) return "$0.00";
    if (usd < 0.01) return `$${usd.toFixed(3)}`;
    return `$${usd.toFixed(2)}`;
  }

  private formatTokens(count: number): string {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1)}M`;
    if (count >= 1_000) return `${(count / 1_000).toFixed(1)}K`;
    return count.toString();
  }

  private formatName(name: string): string {
    return name.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private getCategoryIcon(category: string): string {
    const icons: Record<string, string> = {
      llm_call: "🤖",
      tool_use: "🔧",
      skill_access: "📧",
      message_send: "📤",
      message_receive: "📥",
    };
    return icons[category] ?? "📊";
  }

  private getSkillIcon(skill: string): string {
    const icons: Record<string, string> = {
      gmail: "📧",
      calendar: "📅",
      memory: "🧠",
      web_search: "🔍",
      slack: "💬",
    };
    return icons[skill] ?? "🔌";
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "rax-cost-breakdown": RaxCostBreakdown;
  }
}
